import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package, ChevronRight } from 'lucide-react';
import { useOrders } from '@/hooks/useProducts';
import { formatCurrency, formatDateShort } from '@/lib/utils';
import Badge from '@/components/ui/Badge';
import Pagination from '@/components/ui/Pagination';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Button from '@/components/ui/Button';

type OrderStatus =
  | 'pending'
  | 'processing'
  | 'shipped'
  | 'delivered'
  | 'cancelled'
  | 'refunded';

const statusConfig: Record<
  OrderStatus,
  { variant: 'green' | 'yellow' | 'red' | 'blue' | 'gray' }
> = {
  pending: { variant: 'yellow' },
  processing: { variant: 'blue' },
  shipped: { variant: 'blue' },
  delivered: { variant: 'green' },
  cancelled: { variant: 'red' },
  refunded: { variant: 'gray' },
};

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('account');
  const config = statusConfig[status as OrderStatus] || { variant: 'gray' as const };
  const key = `orders.statuses.${status}` as const;
  const translated = t(key);
  const label = translated === key ? status : translated;
  return <Badge variant={config.variant}>{label}</Badge>;
}

const ORDERS_PER_PAGE = 10;

export default function Orders() {
  const { t } = useTranslation('account');
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useOrders(page);

  const orders = data?.orders ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / ORDERS_PER_PAGE);

  if (isLoading) {
    return <LoadingSpinner className="py-16" />;
  }

  if (isError) {
    return (
      <div className="text-center py-16">
        <p className="text-ink-muted mb-4">
          {t('orders.errorLoading')}
        </p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          {t('common.tryAgain')}
        </Button>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="text-center py-16">
        <Package
          className="h-16 w-16 text-ink-muted mx-auto mb-4"
          aria-hidden="true"
        />
        <h2 className="text-xl font-display font-bold text-ink mb-2">
          {t('orders.emptyHeading')}
        </h2>
        <p className="text-ink-muted mb-6">
          {t('orders.emptyDescription')}
        </p>
        <Link to="/shop">
          <Button>{t('common.browseProducts')}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-display font-bold text-ink">
          {t('orders.history')}
        </h2>
        <p className="text-sm text-ink-muted">
          {t('orders.totalCount', { count: total })}
        </p>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block bg-surface rounded-xl border border-line overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-line bg-surface-soft">
              <th
                scope="col"
                className="text-left text-xs font-semibold text-ink-muted uppercase tracking-wider px-4 py-3"
              >
                {t('orders.table.order')}
              </th>
              <th
                scope="col"
                className="text-left text-xs font-semibold text-ink-muted uppercase tracking-wider px-4 py-3"
              >
                {t('orders.table.date')}
              </th>
              <th
                scope="col"
                className="text-left text-xs font-semibold text-ink-muted uppercase tracking-wider px-4 py-3"
              >
                {t('orders.table.status')}
              </th>
              <th
                scope="col"
                className="text-left text-xs font-semibold text-ink-muted uppercase tracking-wider px-4 py-3"
              >
                {t('orders.table.items')}
              </th>
              <th
                scope="col"
                className="text-right text-xs font-semibold text-ink-muted uppercase tracking-wider px-4 py-3"
              >
                {t('orders.table.total')}
              </th>
              <th scope="col" className="px-4 py-3">
                <span className="sr-only">{t('orders.table.actionsAriaLabel')}</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {orders.map((order: any) => {
              const itemCount = order.item_count ?? order.items?.length ?? 0;
              return (
              <tr
                key={order.id}
                className="hover:bg-surface-soft transition-colors"
              >
                <td className="px-4 py-4">
                  <Link
                    to={`/account/orders/${order.id}`}
                    className="font-medium text-sage-700 hover:text-sage-800 hover:underline"
                  >
                    {t('orders.orderNumberShort', { number: order.order_number })}
                  </Link>
                </td>
                <td className="px-4 py-4 text-sm text-ink-soft">
                  {formatDateShort(order.created_at)}
                </td>
                <td className="px-4 py-4"><StatusBadge status={order.status} /></td>
                <td className="px-4 py-4 text-sm text-ink-soft">
                  {t('common.itemCount', { count: itemCount })}
                </td>
                <td className="px-4 py-4 text-sm font-semibold text-ink text-right">
                  {formatCurrency(Number(order.total))}
                </td>
                <td className="px-4 py-4 text-right">
                  <Link
                    to={`/account/orders/${order.id}`}
                    className="text-ink-muted hover:text-ink-soft inline-flex items-center"
                    aria-label={t('orders.viewOrderAriaLabel', { number: order.order_number })}
                  >
                    <ChevronRight className="h-5 w-5" aria-hidden="true" />
                  </Link>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3">
        {orders.map((order: any) => {
          const itemCount = order.item_count ?? order.items?.length ?? 0;
          return (
          <Link
            key={order.id}
            to={`/account/orders/${order.id}`}
            className="block bg-surface rounded-lg border border-line p-4 hover:border-sage-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-ink">
                {t('orders.orderNumberShort', { number: order.order_number })}
              </span>
              <StatusBadge status={order.status} />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-ink-muted">
                {formatDateShort(order.created_at)} &middot;{' '}
                {t('common.itemCount', { count: itemCount })}
              </span>
              <span className="font-semibold text-ink">
                {formatCurrency(Number(order.total))}
              </span>
            </div>
          </Link>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          className="mt-6 justify-center"
        />
      )}
    </div>
  );
}
